/**
 * GroupsPanel — skupiny GitLabu (gitlab.group_manage) a jejich členové.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ChevronDown, ChevronRight, Plus } from 'lucide-react';
import { Badge, Button, EmptyState, Input } from '../../../components/ui';
import { Select } from '../../../components/ui/Select';
import { callWidgetTool } from '../../../widgets/scope';
import type { EffectiveScope, WidgetRenderProps } from '../../../widgets/types';
import { MembersBlock } from './MembersBlock';

interface Group {
  id: number;
  path: string;
  visibility?: string;
  project_creation_level?: string;
}
interface GroupsResp {
  ok?: boolean;
  error?: string;
  groups?: Group[];
}
interface ConnectionsResp {
  ok?: boolean;
  servers?: Array<{ id: string; host: string; has_token: boolean }>;
}

function nodeKey(scope: EffectiveScope): string {
  return scope.node ?? 'local';
}

export function GroupsPanel({ scope }: WidgetRenderProps = {} as WidgetRenderProps) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const sc: EffectiveScope = scope ?? { node: null, identity: null };
  const nk = nodeKey(sc);
  const [connection, setConnection] = useState('');
  const [openPath, setOpenPath] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState('');
  const [path, setPath] = useState('');
  const [visibility, setVisibility] = useState('private');

  const conns = useQuery<ConnectionsResp>({
    queryKey: ['gitlab-connections', nk],
    queryFn: () => callWidgetTool<ConnectionsResp>('gitlab.connections_list', {}, sc, { silent: true }),
    staleTime: 60_000,
  });
  const servers = (conns.data?.servers ?? []).filter((s) => s.has_token);
  const effectiveConn = connection || servers[0]?.id || '';
  const connArg = effectiveConn ? { connection_id: effectiveConn } : {};

  const q = useQuery<GroupsResp>({
    queryKey: ['gitlab-groups', nk, effectiveConn],
    queryFn: () =>
      callWidgetTool<GroupsResp>('gitlab.group_manage', { action: 'list', ...connArg }, sc, { silent: true }),
    enabled: conns.isSuccess,
  });

  const createMut = useMutation({
    mutationFn: () =>
      callWidgetTool<{ ok?: boolean; error?: string; path?: string }>(
        'gitlab.group_manage',
        { action: 'create', name: name.trim(), path: path.trim() || name.trim(), visibility, ...connArg },
        sc,
      ),
    onSuccess: (r) => {
      if (r?.ok === false) {
        toast.error(r.error ?? t('gitlab.groups.createFailed'));
        return;
      }
      toast.success(t('gitlab.groups.created', { name: r.path ?? name }));
      setName('');
      setPath('');
      setCreateOpen(false);
      qc.invalidateQueries({ queryKey: ['gitlab-groups', nk, effectiveConn] });
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const groups = q.data?.groups ?? [];

  return (
    <div className='p-3 space-y-3'>
      <div className='flex flex-wrap items-center justify-between gap-2'>
        <span className='text-xs text-zinc-400'>
          {t('gitlab.groups.title', { count: groups.length })}
        </span>
        <div className='flex items-center gap-2'>
          {servers.length > 1 && (
            <Select
              value={effectiveConn}
              onChange={(e) => setConnection((e.target as HTMLSelectElement).value)}
              className='text-xs'
            >
              {servers.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.id} · {s.host.replace(/^https?:\/\//, '')}
                </option>
              ))}
            </Select>
          )}
          <Button variant='secondary' size='sm' onClick={() => setCreateOpen((v) => !v)}>
            <Plus size={13} /> {t('gitlab.groups.new')}
          </Button>
        </div>
      </div>

      {createOpen && (
        <div className='rounded-lg border border-zinc-800 p-3 space-y-2'>
          <div className='flex flex-col sm:flex-row gap-2'>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('gitlab.groups.namePlaceholder')}
              className='flex-1'
            />
            <Input
              value={path}
              onChange={(e) => setPath(e.target.value)}
              placeholder={t('gitlab.groups.pathPlaceholder')}
              className='flex-1'
            />
            <Select
              size='sm'
              value={visibility}
              onChange={(e) => setVisibility((e.target as HTMLSelectElement).value)}
              options={[
                { value: 'private', label: t('gitlab.groups.visPrivate') },
                { value: 'internal', label: t('gitlab.groups.visInternal') },
                { value: 'public', label: t('gitlab.groups.visPublic') },
              ]}
            />
            <Button
              variant='primary'
              size='sm'
              loading={createMut.isPending}
              disabled={!name.trim()}
              onClick={() => createMut.mutate()}
            >
              {t('gitlab.groups.create')}
            </Button>
          </div>
        </div>
      )}

      {conns.isSuccess && servers.length === 0
        ? (
          <EmptyState
            title={t('gitlab.users.noConnectionTitle')}
            description={t('gitlab.users.noConnectionDesc')}
          />
        )
        : q.isLoading || conns.isLoading
        ? <div className='text-sm text-zinc-500'>{t('common.loading')}</div>
        : q.data?.ok === false
        ? <div className='text-sm text-red-400'>{q.data.error ?? t('gitlab.groups.loadFailed')}</div>
        : groups.length === 0
        ? <EmptyState title={t('gitlab.groups.emptyTitle')} description={t('gitlab.groups.emptyDesc')} />
        : (
          <div className='space-y-2'>
            {groups.map((g) => {
              const open = openPath === g.path;
              return (
                <div key={g.id} className='rounded-lg border border-zinc-800'>
                  <button
                    type='button'
                    className='w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-zinc-900/40'
                    onClick={() => setOpenPath(open ? null : g.path)}
                  >
                    {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    <span className='font-mono text-sm text-zinc-100 truncate'>{g.path}</span>
                    {g.visibility && <Badge>{g.visibility}</Badge>}
                    <span className='ml-auto text-xs text-zinc-500'>
                      {t('gitlab.members.open')}
                    </span>
                  </button>
                  {open && (
                    <div className='px-3 pb-3'>
                      <MembersBlock
                        scope={sc}
                        connectionId={effectiveConn || undefined}
                        target='group'
                        targetRef={g.path}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
    </div>
  );
}
